import { useNuxtApp, type AsyncDataOptions } from '#app'
import { useDayjs } from '#imports'

export const useQueryCache = <TData = any>() => {
    const nuxtApp = useNuxtApp()
    const dayjs = useDayjs()

    function get(key: string) {
        const data: TData | undefined = nuxtApp.payload.data[key] ?? nuxtApp.static.data[key]

        // No data
        if (!data) {
            return
        }

        const cache = nuxtApp.payload.data['queryCache']?.[key]

        if (!cache?.expiresAt) return

        const now = dayjs.utc()
        const expiresAt = dayjs.utc(cache.expiresAt)
        const isExpired = now >= expiresAt

        if (isExpired) return

        return data as TData
    }

    function unset(keys: string | string[]) {
        const _handler = (key: string) => {
            const data = nuxtApp.payload.data?.['queryCache']?.[key]

            if (!data) return

            nuxtApp.payload.data['queryCache'][key] = undefined
        }

        if (Array.isArray(keys)) {
            keys.forEach(key => _handler(key))
            return
        }

        _handler(keys)
    }

    /**
     * Helper function for setting up `getCachedData` in `useAsyncData`'s options.
     * @param key A key
     */
    const getCachedDataHandler: AsyncDataOptions<TData>['getCachedData'] = (key, _, context) => {
        if (context.cause === 'refresh:manual') return
        if (context.cause === 'refresh:hook') return

        const cachedData = get(key)

        return cachedData
    }

    function transformHandler(key: string, input: TData, ttl: number): TData {
        const now = dayjs.utc()

        const cache = {
            expiresAt: now.add(dayjs.duration({ seconds: ttl })).toISOString(),
        }

        nuxtApp.payload.data['queryCache'] ||= {}
        nuxtApp.payload.data['queryCache'][key] = cache

        return input
    }

    return {
        get,
        unset,
        getCachedDataHandler,
        transformHandler,
    }
}
